import React from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  CardActionArea,
  Grid,
} from '@mui/material';
import { useQuery } from 'react-query';
import apiClient from '@uniqcamp/shared/services/api.service';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { GatesResponse } from '../types';
import { requestFullscreen } from '../utils/fullscreen';

function GateList({
  schoolCode,
  otp,
  onSelectGate,
  onBack,
}: {
  schoolCode: string;
  otp: string;
  onSelectGate: (gateId: string) => void;
  onBack: () => void;
}) {
  const otpParam = otp ? `?otp=${encodeURIComponent(otp)}` : '';
  const { data, isLoading, isError, error } = useQuery<GatesResponse>(
    ['tv-display-gates', schoolCode],
    async () => {
      const res = await apiClient.get(`/tv-display/${encodeURIComponent(schoolCode)}${otpParam}`);
      return res.data as GatesResponse;
    },
    { enabled: !!schoolCode }
  );

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography>Loading gates…</Typography>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography color="error">{(error as { message?: string })?.message || 'Failed to load gates'}</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mt: 2 }}>
          Back
        </Button>
      </Container>
    );
  }

  const gates = data?.gates ?? [];
  const schoolName = data?.schoolName ?? '';
  const useTvDisplay = data?.useTvDisplay !== false;

  if (!useTvDisplay) {
    return (
      <Container maxWidth="md" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
          }}
        >
          <Typography variant="h5" color="text.secondary" sx={{ fontWeight: 600 }}>
            Display is disabled
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            TV display is turned off in Principal Settings. Enable &quot;Use TV display&quot; to use this screen.
          </Typography>
          <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mt: 2 }}>
            Change school
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
        Change school
      </Button>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        {schoolName}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Select a gate for this display
      </Typography>
      {gates.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No gates configured for this school.</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {gates.map((g) => (
            <Grid item xs={12} sm={6} md={4} key={g.gateId}>
              <Card sx={{ height: '100%' }}>
                <CardActionArea
                  onClick={() => {
                    requestFullscreen();
                    onSelectGate(g.gateId);
                  }}
                  sx={{ height: '100%', display: 'flex', alignItems: 'stretch' }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <MeetingRoomIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="h6" fontWeight={600}>
                        {g.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Gate
                      </Typography>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
}

export default GateList;
